/**
 * Central registry of every link used in the app.
 *
 * - ROUTES          : client-side navigation paths (navigate(), <Link to>, nav config)
 * - ROUTE_PATTERNS  : react-router <Route path> patterns (App.tsx)
 * - API             : backend endpoints, relative to the axios baseURL (which already includes /api/v1)
 *
 * Never hardcode a path string elsewhere; add it here and import it.
 */

const enc = encodeURIComponent;
const libraryQuery = (sensorType: string, category: string) => `sensor_type=${enc(sensorType)}&category=${enc(category)}`;

// ---------------------------------------------------------------------------
// Client routes
// ---------------------------------------------------------------------------
export const ROUTES = {
  ROOT: '/',
  LOGIN: '/login',
  UNAUTHORIZED: '/unauthorized',

  CALIBRATIONS: '/calibrations',
  CALIBRATIONS_START: '/calibrations/start',
  CALIBRATIONS_BULK_START: '/calibrations/bulk-start',
  CALIBRATIONS_DETAILS: '/calibrations/details',

  FORMS: '/forms',
  formRuntime: (formId: string) => `/forms/${enc(formId)}`,

  MANAGE: '/manage',
  FORM_BUILDER: '/manage/form-builder',
  FORM_BUILDER_NEW: '/manage/form-builder/new',
  FORM_BUILDER_LIVE: '/manage/form-builder?status=published',
  formBuilderEdit: (formId: string) => `/manage/form-builder/${enc(formId)}`,
  formBuilderVersions: (formId: string) => `/manage/form-builder/${enc(formId)}/versions`,
  formBuilderAudit: (formId: string) => `/manage/form-builder/${enc(formId)}/audit`,
  DYNAMIC_FIELDS: '/manage/dynamic-fields',
  /** The library of dynamic fields is identified by sensor type + category (query string, so no path encoding clashes). */
  dynamicFieldsEdit: (sensorType: string, category: string) => `/manage/dynamic-fields/edit?${libraryQuery(sensorType, category)}`,
  dynamicFieldsVersions: (sensorType: string, category: string) => `/manage/dynamic-fields/versions?${libraryQuery(sensorType, category)}`,
  dynamicFieldsAudit: (sensorType: string, category: string) => `/manage/dynamic-fields/audit?${libraryQuery(sensorType, category)}`,
  MANAGE_ACCEPTANCE_CRITERIA: '/manage/acceptance-criteria',
  MANAGE_DAILY_ENV_DATA: '/manage/daily-environmental-data',
} as const;

// ---------------------------------------------------------------------------
// react-router <Route path> patterns (nested under "/" in App.tsx, so relative)
// ---------------------------------------------------------------------------
export const ROUTE_PATTERNS = {
  LOGIN: '/login',
  UNAUTHORIZED: '/unauthorized',
  ROOT: '/',
  CALIBRATIONS: 'calibrations',
  CALIBRATIONS_START: 'calibrations/start',
  CALIBRATIONS_BULK_START: 'calibrations/bulk-start',
  CALIBRATIONS_DETAILS: 'calibrations/details',
  FORM_BUILDER: 'manage/form-builder',
  FORM_BUILDER_NEW: 'manage/form-builder/new',
  FORM_BUILDER_AUDIT: 'manage/form-builder/:formId/audit',
  FORM_BUILDER_VERSIONS: 'manage/form-builder/:formId/versions',
  FORM_BUILDER_EDIT: 'manage/form-builder/:formId',
  DYNAMIC_FIELDS: 'manage/dynamic-fields',
  DYNAMIC_FIELDS_EDIT: 'manage/dynamic-fields/edit',
  DYNAMIC_FIELDS_VERSIONS: 'manage/dynamic-fields/versions',
  DYNAMIC_FIELDS_AUDIT: 'manage/dynamic-fields/audit',
  FORMS: 'forms',
  FORM_RUNTIME: 'forms/:formId',
  MANAGE_ACCEPTANCE_CRITERIA: 'manage/acceptance-criteria',
  MANAGE_DAILY_ENV_DATA: 'manage/daily-environmental-data',
  NOT_FOUND: '*',
} as const;

// ---------------------------------------------------------------------------
// API endpoints (relative to axios baseURL)
// ---------------------------------------------------------------------------
const FORM_BUILDER = '/form-builder';
const CALIBRATION_FORMS = '/calibration-forms';
const DYNAMIC_FIELD_LIBRARY = `${CALIBRATION_FORMS}/dynamic-field-library`;

export const API = {
  formBuilder: {
    forms: `${FORM_BUILDER}/forms`,
    form: (key: string) => `${FORM_BUILDER}/forms/${enc(key)}`,
    duplicate: (key: string) => `${FORM_BUILDER}/forms/${enc(key)}/duplicate`,
    publish: (key: string) => `${FORM_BUILDER}/forms/${enc(key)}/publish`,
    restore: (key: string) => `${FORM_BUILDER}/forms/${enc(key)}/restore`,
    versions: (key: string) => `${FORM_BUILDER}/forms/${enc(key)}/versions`,
    version: (key: string, version: number) => `${FORM_BUILDER}/forms/${enc(key)}/versions/${version}`,
    versionDiff: (key: string, version: number) => `${FORM_BUILDER}/forms/${enc(key)}/versions/${version}/diff`,
    rollback: (key: string, version: number) => `${FORM_BUILDER}/forms/${enc(key)}/versions/${version}/rollback`,
    auditLogs: (key: string) => `${FORM_BUILDER}/forms/${enc(key)}/audit-logs`,
    runtimeForms: `${FORM_BUILDER}/runtime/forms`,
    runtime: (key: string) => `${FORM_BUILDER}/runtime/${enc(key)}`,
    runtimeSubmit: (key: string) => `${FORM_BUILDER}/runtime/${enc(key)}/submit`,
    runtimeValidate: (key: string) => `${FORM_BUILDER}/runtime/${enc(key)}/validate`,
    /** `draft` resolves against the unpublished draft instead of the published runtime form. */
    dataSource: (key: string, fieldKey: string, draft = false) =>
      `${draft ? `${FORM_BUILDER}/forms/${enc(key)}` : `${FORM_BUILDER}/runtime/${enc(key)}`}/data-source/${enc(fieldKey)}`,
    stepDynamicSchema: (key: string, stepId: string, draft = false) =>
      `${draft ? `${FORM_BUILDER}/forms/${enc(key)}` : `${FORM_BUILDER}/runtime/${enc(key)}`}/steps/${enc(stepId)}/dynamic-schema`,
  },

  calibrationForms: {
    /** Includes the /api/v1 prefix: stored as a literal endpoint inside form schemas. */
    calibrationDataSchemaFull: '/api/v1/calibration-forms/calibration-data/schema',
    calibrationDataSchema: `${CALIBRATION_FORMS}/calibration-data/schema`,
    /** One request returns every select collection: sensorType, category, displacementSensorType, model, range. */
    optionsCombined: `${CALIBRATION_FORMS}/options/combined`,
  },

  /** Library of dynamic fields, one per sensor type + category (both sent as query params). */
  dynamicFieldLibrary: {
    libraries: `${CALIBRATION_FORMS}/dynamic-field-libraries`,
    library: DYNAMIC_FIELD_LIBRARY,
    entries: `${DYNAMIC_FIELD_LIBRARY}/entries`,
    publish: `${DYNAMIC_FIELD_LIBRARY}/publish`,
    draft: `${DYNAMIC_FIELD_LIBRARY}/draft`,
    versions: `${DYNAMIC_FIELD_LIBRARY}/versions`,
    version: (version: number) => `${DYNAMIC_FIELD_LIBRARY}/versions/${version}`,
    versionDiff: (version: number) => `${DYNAMIC_FIELD_LIBRARY}/versions/${version}/diff`,
    rollback: (version: number) => `${DYNAMIC_FIELD_LIBRARY}/versions/${version}/rollback`,
    auditLogs: `${DYNAMIC_FIELD_LIBRARY}/audit-logs`,
  },

  sensors: {
    list: '/sensors',
    byId: (id: string | number) => `/sensors/${id}`,
    search: '/sensors/search',
    statisticsOverview: '/sensors/statistics/overview',
    calibrated: '/sensors/calibrated',
    bulkCalibrated: '/sensors/bulk-calibrated',
  },

  calibrations: {
    list: '/calibrations',
    byId: (id: string | number) => `/calibrations/${id}`,
  },

  certificates: {
    list: '/certificates',
    changeReasons: '/certificates/change-reasons',
    generate: '/certificates/generate',
    preview: '/certificates/preview',
    download: (id: string | number) => `/certificates/${id}/download`,
  },

  sensorTypes: {
    list: '/sensor-types',
    options: '/sensor-types/options',
    byId: (id: string | number) => `/sensor-types/${id}`,
    acceptanceCriteria: (id: string | number) => `/sensor-types/${id}/acceptance-criteria`,
  },

  results: {
    list: '/results',
  },
} as const;

/** Default endpoint for the form builder's step-level dynamic schema. */
export const DEFAULT_DYNAMIC_FIELDS_SCHEMA_ENDPOINT = API.calibrationForms.calibrationDataSchemaFull;
